import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ADMIN_UID } from './utils/constants';

// Layouts (Mantenemos UserApp estático para renderizado inmediato del shell)
import UserApp from './modules/user/UserApp';

// Views críticas (Inicio - Mantenemos estático para LCP rápido)
import BettingLobby from './modules/betting/pages/BettingLobby';

// Lazy Loaded Modules & Pages
// Admin
const AdminApp = lazy(() => import('./modules/admin/AdminApp'));

// Sports
const SportsView = lazy(() => import('./modules/sports/pages/SportsView'));
const MatchDetailPage = lazy(() => import('./modules/sports/pages/MatchDetailPage'));

// Betting & Finance
const MyBetsPage = lazy(() => import('./modules/betting/pages/MyBetsPage'));
const DepositPage = lazy(() => import('./modules/finance/pages/DepositPage'));
const TransactionsPage = lazy(() => import('./modules/finance/pages/TransactionsPage'));

// User Profile
const ProfilePage = lazy(() => import('./modules/user/pages/ProfilePage'));
const ProfileStats = lazy(() => import('./modules/user/pages/ProfileStats'));

// Casino Section & Games
const CasinoSection = lazy(() => import('./modules/casino/CasinoSection'));
const FortuneTigerGame = lazy(() => import('./modules/casino/FortuneTigerGame'));
const CrashGame = lazy(() => import('./modules/casino/CrashGame'));
const BlackjackGame = lazy(() => import('./modules/casino/BlackjackGame'));
const RouletteGame = lazy(() => import('./modules/casino/RouletteGame'));
const SlotsGame = lazy(() => import('./modules/casino/SlotsGame'));
const PrimeMinesPro = lazy(() => import('./modules/casino/PrimeMinesPro'));
const SpaceCrashPrime = lazy(() => import('./modules/casino/SpaceCrashPrime'));
const VortexFlyPrime = lazy(() => import('./modules/casino/VortexFlyPrime'));

// Game Wrapper
const GameWrapper = lazy(() => import('./components/GameWrapper'));

// Legal & Support Views
const TermsView = lazy(() => import('./modules/legal/pages/TermsView'));
const AmlKycView = lazy(() => import('./modules/legal/pages/AmlKycView'));
const PrivacyView = lazy(() => import('./modules/legal/pages/PrivacyView'));
const ResponsibleGamingView = lazy(() => import('./modules/legal/pages/ResponsibleGamingView'));
const RulesView = lazy(() => import('./modules/legal/pages/RulesView'));
const CookiesView = lazy(() => import('./modules/legal/pages/CookiesView'));
const HelpCenterView = lazy(() => import('./modules/support/pages/HelpCenterView'));

// Loading Screen Component
const LoadingScreen = () => (
    <div className="min-h-screen bg-[#171717] flex flex-col items-center justify-center gap-4">
        <img src="/assets/branding/LogoOficial.png" alt="Loading" className="w-24 h-24 mb-4 rounded-[1.5rem] shadow-2xl animate-pulse" />
    </div>
);

const CasinoRoute = ({ children, orientation = 'any' }) => {
    return (
        <GameWrapper orientation={orientation} onClose={() => window.history.back()}>
            {children}
        </GameWrapper>
    );
};

const AdminRoute = ({ children }) => {
    const { user, userProfile, loading } = useAuth();

    if (loading) return <LoadingScreen />;

    const isOwner = user?.uid === ADMIN_UID;
    const isProfileAdmin = userProfile?.isAdmin === true || userProfile?.role === 'admin';

    if (!user || (!isOwner && !isProfileAdmin)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

export default function AppRouter() {
    const { loading } = useAuth();

    if (loading) return <LoadingScreen />;

    return (
        <Suspense fallback={<LoadingScreen />}>
            <Routes>
                <Route element={<UserApp />}>
                    <Route path="/" element={<BettingLobby />} />
                    <Route path="/home" element={<Navigate to="/" replace />} />
                    <Route path="/sports" element={<SportsView />} />
                    <Route path="/deportes" element={<Navigate to="/sports" replace />} />
                    <Route path="/live" element={<Navigate to="/" replace />} />

                    {/* Protected Routes */}
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/profile/stats" element={<ProfileStats />} />
                    <Route path="/deposit" element={<DepositPage />} />
                    <Route path="/withdraw" element={<DepositPage />} />
                    <Route path="/retirar" element={<DepositPage />} />
                    <Route path="/mybets" element={<MyBetsPage />} />
                    <Route path="/history" element={<TransactionsPage />} />
                    <Route path="/match/:id" element={<MatchDetailPage />} />
                    <Route path="/casino" element={<CasinoSection />} />

                    {/* Legal Routes */}
                    <Route path="/terms" element={<TermsView />} />
                    <Route path="/terminos" element={<TermsView />} />
                    <Route path="/aml" element={<AmlKycView />} />
                    <Route path="/kyc" element={<AmlKycView />} />
                    <Route path="/aml-kyc" element={<AmlKycView />} />
                    <Route path="/privacy" element={<PrivacyView />} />
                    <Route path="/privacidad" element={<PrivacyView />} />
                    <Route path="/responsible-gaming" element={<ResponsibleGamingView />} />
                    <Route path="/juego-responsable" element={<ResponsibleGamingView />} />
                    <Route path="/rules" element={<RulesView />} />
                    <Route path="/reglas" element={<RulesView />} />
                    <Route path="/cookies" element={<CookiesView />} />

                    {/* Help Center Routes */}
                    <Route path="/help/faq" element={<HelpCenterView />} />
                    <Route path="/help/support" element={<HelpCenterView />} />
                    <Route path="/help/guide" element={<HelpCenterView />} />
                    <Route path="/help/payments" element={<HelpCenterView />} />
                    <Route path="/ayuda" element={<HelpCenterView />} />
                </Route>

                {/* RUTAS SEGURAS DE ADMINISTRADOR */}
                <Route path="/admin/*" element={
                    <AdminRoute>
                        <AdminApp />
                    </AdminRoute>
                } />

                {/* Casino Games */}
                <Route path="/casino/fortune-tiger" element={
                    <LazyGameComponent component={FortuneTigerGame} />
                } />
                <Route path="/casino/crash" element={<CasinoRoute><LazyGameComponent component={CrashGame} /></CasinoRoute>} />
                <Route path="/casino/blackjack" element={<CasinoRoute><LazyGameComponent component={BlackjackGame} /></CasinoRoute>} />
                <Route path="/casino/roulette" element={<CasinoRoute><LazyGameComponent component={RouletteGame} /></CasinoRoute>} />
                <Route path="/casino/slots" element={<CasinoRoute><LazyGameComponent component={SlotsGame} /></CasinoRoute>} />
                <Route path="/casino/mines" element={<CasinoRoute><LazyGameComponent component={PrimeMinesPro} /></CasinoRoute>} />
                <Route path="/casino/space-crash" element={<CasinoRoute><LazyGameComponent component={SpaceCrashPrime} /></CasinoRoute>} />
                <Route path="/casino/vortex-fly" element={<CasinoRoute><LazyGameComponent component={VortexFlyPrime} /></CasinoRoute>} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

// Helper para componentes que necesitan props del contexto pero se renderizan lazy fuera del layout principal
const LazyGameComponent = ({ component: Component }) => {
    const { user, userProfile } = useAuth();
    
    const activeCurrency = userProfile?.activeCurrency || 'BS';
    const balance = activeCurrency === 'USDT' 
        ? (userProfile?.balanceUsdt || 0)
        : (parseFloat(userProfile?.balanceBs || 0) + parseFloat(userProfile?.bonusBalance || 0));

    return <Component
        user={user}
        userProfile={userProfile}
        balance={balance}
        activeCurrency={activeCurrency}
        onBack={() => window.history.back()}
    />;
};
